import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = ["Under $2k / month", "$2k – $5k / month", "$5k – $15k / month", "$15k+ / month", "Not sure yet"];

const EMPTY = { name: "", email: "", company: "", budget: "", message: "" };

const inputCls =
    "w-full border-2 border-ink/20 bg-white/70 px-3 py-2.5 text-sm text-ink outline-none transition-colors duration-300 placeholder:text-ink/35 focus:border-inkblue";

export const EnquiryModal = ({ open, onOpenChange }) => {
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Name and email are required.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            toast.success("You're in the queue. We'll reach out within 24 hours.");
            setForm(EMPTY);
            onOpenChange(false);
        } catch {
            toast.error("Something glitched — please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="enquiry-modal"
                className="max-h-[90vh] overflow-y-auto rounded-none border-2 border-ink bg-paper shadow-[8px_8px_0_#14171C] sm:max-w-lg"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-bold tracking-tight text-ink">
                        Start Your <span className="highlighter px-1">Queue</span>
                    </DialogTitle>
                    <DialogDescription className="text-sm text-ink/60">
                        Tell us where you are — we'll map the shortest route from traffic to customers.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <label className="mb-1.5 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink/60">Name *</label>
                            <input
                                data-testid="enquiry-name-input"
                                className={inputCls}
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Ada Lovelace"
                            />
                        </div>
                        <div>
                            <label className="mb-1.5 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink/60">Email *</label>
                            <input
                                data-testid="enquiry-email-input"
                                type="email"
                                className={inputCls}
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="ada@company.com"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="mb-1.5 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink/60">Company</label>
                        <input
                            data-testid="enquiry-company-input"
                            className={inputCls}
                            value={form.company}
                            onChange={(e) => setForm({ ...form, company: e.target.value })}
                            placeholder="Company Ltd."
                        />
                    </div>
                    <div>
                        <label className="mb-1.5 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink/60">Monthly budget</label>
                        <select
                            data-testid="enquiry-budget-select"
                            className={inputCls}
                            value={form.budget}
                            onChange={(e) => setForm({ ...form, budget: e.target.value })}
                        >
                            <option value="">Select a range…</option>
                            {BUDGETS.map((b) => (
                                <option key={b} value={b}>{b}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1.5 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink/60">Anything else?</label>
                        <textarea
                            data-testid="enquiry-message-input"
                            rows={3}
                            className={`${inputCls} resize-none`}
                            value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            placeholder="Current traffic, goals, timelines…"
                        />
                    </div>
                    <button
                        data-testid="enquiry-submit-button"
                        type="submit"
                        disabled={loading}
                        className="btn-primary mt-1 flex items-center justify-center gap-2.5 border-2 border-ink bg-ink px-6 py-3.5 font-mono text-sm font-bold uppercase tracking-wider text-paper disabled:opacity-60"
                    >
                        {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
                        {loading ? "Joining the queue…" : "Join the Queue"}
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
};
